import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Select } from "@amzn/stencil-react-components/select";
import Input from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useLists } from "hooks/ListsContext";
import { Visibility } from "lib/models/list-models";
import { UserPermissions } from "lib/services";
import React, { FunctionComponent, useState } from "react";

interface ListModalProps {
  onClose: () => void;
  initialData?: { name?: string; description?: string; visibility?: Visibility; allowedDomains?: string[] };
  listId?: string;
  isEdit?: boolean;
}

const visibilityOptions = [
  { label: "Private", value: "private" },
  { label: "Shared", value: "shared" },
];

const ListModal: FunctionComponent<ListModalProps> = ({ onClose, initialData, listId, isEdit = false }) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [visibility, setVisibility] = useState<Visibility>(initialData?.visibility || Visibility.PRIVATE);
  const domains = initialData?.allowedDomains || [window.location.href.includes("aiforme") ? "aiforme" : "software"];

  const { createList, updateList, getMyLists } = useLists();
  const { addMessageBanner } = useMessageBannersBox();

  const handleSubmit = async () => {
    const data = { name, description, visibility, allowedDomains: domains };
    try {
      if (isEdit && listId) {
        await updateList(listId, data);
        addMessageBanner(getString("lists.modal.updateSuccess"), MessageBannerType.Success);
      } else {
        await createList(data);
        addMessageBanner(getString("lists.modal.createSuccess"), MessageBannerType.Success);
      }
      getMyLists({
        includeItemDetails: true,
        visibility: Visibility.PRIVATE,
      });
    } catch (error) {
      addMessageBanner(
        getString(isEdit ? "lists.modal.updateError" : "lists.modal.createError"),
        MessageBannerType.Error
      );
    } finally {
      onClose();
    }
  };

  const isValid = name.trim().length > 0;

  return (
    <ModalContent
      titleText={getString(isEdit ? "lists.modal.editTitle" : "lists.modal.title")}
      maxWidth="800px"
      buttons={[
        <Button key="cancel" variant={ButtonVariant.Tertiary} onClick={onClose}>
          {getString("lists.modal.cancel")}
        </Button>,
        <Button key="submit" variant={ButtonVariant.Primary} disabled={!isValid} onClick={handleSubmit}>
          {getString(isEdit ? "lists.modal.update" : "lists.modal.create")}
        </Button>,
      ]}
    >
      <Col gridGap="S300" width={"500px"}>
        <InputWrapper id="list-name" labelText={getString("lists.modal.name")} required={true}>
          {(inputProps) => (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={getString("lists.modal.namePlaceholder")}
              touchpoint="list-name-input"
              {...inputProps}
            />
          )}
        </InputWrapper>

        <InputWrapper id="list-description" labelText={getString("lists.modal.description")}>
          {(inputProps) => (
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={getString("lists.modal.descriptionPlaceholder")}
              rows={3}
              touchpoint="list-description-textarea"
              {...inputProps}
            />
          )}
        </InputWrapper>

        <InputWrapper id="list-visibility" labelText={getString("lists.modal.visibility")}>
          {(inputProps) => (
            <Select
              value={visibilityOptions.find((opt) => opt.value === visibility)}
              onChange={(option) => setVisibility(option.value as Visibility)}
              options={visibilityOptions}
              valueAccessor="value"
              renderOption={(option) => option.label}
              renderSelectedValue={(option) => option.label}
              {...inputProps}
            />
          )}
        </InputWrapper>
      </Col>
    </ModalContent>
  );
};

export default ListModal;
